/* eslint-disable no-restricted-imports */
import { FC, useCallback, useMemo } from 'react';
import throttle from 'lodash/throttle';
import { toPostUI } from '../../../mappers/posts/toPostUI';
import { withPostCallbacks } from '../../../routing/post/withPostCallbacks';
import { SearchNotFound } from '../../../routing/search/SearchNotFound';
import { useEventSource } from '../../../utils/LocationContext';
import { EventSource } from '../../../utils/analyticsService';
import { useIsMobile } from '../../../utils/getIsMobile';
import { useGridImageOptimizer } from '../../../utils/mds/useGridImageAlias';
import { useTranslation } from '../../../utils/useTranslation';
import { IconClose } from '../../icon/IconClose';
import { IconBookmarkCheck } from '../../icon/iconBookmarkCheck';
import { IconBookmarkPlusOutline } from '../../icon/iconBookmarkPlusOutline';
import { Button } from '../../input/Button';
import { IconButton } from '../../input/IconButton';
import { Input } from '../../input/Input';
import { Box } from '../../layout/Box';
import { DataTestId } from '../../layout/DataTestIdWrapper';
import { Flex } from '../../layout/Flex';
import { ResizableGrid } from '../../layout/ResizableGrid/ResizableGrid';
import { PostCardGrid } from '../../posts/PostCardGrid/PostCardGrid';
import { calculateGridCardHeight } from '../../posts/PostCardGrid/utils';
import { Typography } from '../../typography/Typography';
import { CreateCollectionContentProps } from './types';

const BOTTOM_Y_MARGIN = 124;

const THROTTLE_TIMEOUT = 200;

export const CreateCollectionContent: FC<CreateCollectionContentProps> = ({
    selectedPosts,
    onSelectPost,
    inputText,
    onInputChange,
    postCollection,
    onPostCardClick,
    modalContentRef,
    onReport,
    atTop,
}) => {
    const t = useTranslation();
    const isMobile = useIsMobile();

    const { eventSource } = useEventSource();

    const { getAlias: getGridImageAliasForCard } = useGridImageOptimizer();

    const EnchancedPostGridCardCompact = useMemo(
        () => withPostCallbacks(PostCardGrid, eventSource ?? EventSource.MULTICOLUMN),
        [eventSource],
    );

    // Убираем фокус со строки поиска, чтобы скрывалась клавиатура на ios INSPIRE-5846
    const onScroll = throttle(() => {
        const activeElement = document.activeElement as HTMLInputElement;

        if (activeElement && isMobile) {
            activeElement.blur();
        }
    }, THROTTLE_TIMEOUT);

    const getItemHeight = (index: number, width: number) => {
        const postModel = postCollection.items[index];
        if (!postModel) {
            return 0;
        }

        return calculateGridCardHeight(width, toPostUI(postModel).content, postModel.postType);
    };

    const renderItem = useCallback(
        (
            index: number,
            columns: number,
            isVisible: boolean | undefined,
            cardSize?: { width: number; height: number },
        ) => {
            const postModel = postCollection.items[index];
            if (!postModel) {
                return null;
            }

            const isSelected = selectedPosts.includes(postModel.id);
            const imageAlias = getGridImageAliasForCard(cardSize?.width);

            return (
                <EnchancedPostGridCardCompact
                    key={postModel.id}
                    post={postModel}
                    imageAlias={imageAlias}
                    shareDisabled
                    businessPageDisabled
                    index={index}
                    gridColumns={columns}
                    isVisible={isVisible}
                    onClick={onPostCardClick}
                    onReport={onReport ? () => onReport(postModel.id) : undefined}
                    onBusinessClick={() => {
                        onPostCardClick({ postNumber: index });
                    }}
                    actionsBottomOffset={4.5}
                    addonAfter={
                        <Box
                            position="absolute"
                            backgroundColor={'var(--background-primary)'}
                            bottom="39px"
                            right={0}
                            left={0}
                            height={36}
                            borderRadius="0px 0px 16px 16px"
                        >
                            <Button
                                dataTestId={DataTestId.CREATE_COLLECTION_ADD_POST}
                                width="100%"
                                height={36}
                                variant="filled"
                                color={isSelected ? 'primary' : 'secondary'}
                                borderRadius="0px 0px 16px 16px"
                                onClick={() => onSelectPost(postModel.id)}
                            >
                                {isSelected ? (
                                    <IconBookmarkCheck size={16} />
                                ) : (
                                    <IconBookmarkPlusOutline size={16} />
                                )}
                                <Typography
                                    color={isSelected ? 'invert' : 'primary'}
                                    ml={0.5}
                                    weight="bold"
                                    variant="caption"
                                >
                                    {isSelected ? 'Добавлено' : 'Добавить'}
                                </Typography>
                            </Button>
                        </Box>
                    }
                    hiddenActions
                />
            );
        },
        [
            postCollection.items,
            selectedPosts,
            EnchancedPostGridCardCompact,
            onPostCardClick,
            onSelectPost,
            onReport,
            getGridImageAliasForCard,
        ],
    );

    return (
        <Flex width="100%" height="100%">
            <Flex width="100%" height="100%" direction="column" alignItems="stretch">
                <Box
                    pb={1}
                    px={isMobile ? 3 : 0}
                    width="100%"
                    borderBottom={!atTop ? '1px solid var(--divider)' : ''}
                >
                    <Input
                        value={inputText}
                        variant="filled"
                        placeholder="найти посты для коллекции"
                        onInput={(event) => {
                            onInputChange(event.currentTarget.value);
                        }}
                        endAdornment={
                            inputText ? (
                                <Box alignSelf="center">
                                    <IconButton
                                        size="xxs"
                                        variant="filled"
                                        onClick={() => onInputChange('')}
                                    >
                                        <IconClose />
                                    </IconButton>
                                </Box>
                            ) : undefined
                        }
                    />
                </Box>
                <Flex
                    direction="column"
                    ref={modalContentRef}
                    width="100%"
                    height="100%"
                    overflowY="auto"
                    scrollbarGutter="stable"
                    onScroll={onScroll}
                    px={isMobile ? 3 : 0}
                >
                    <Typography py={1.5} variant="title3" weight="bold">
                        {t(inputText ? 'Результаты поиска' : 'Идеи для коллекции')}
                    </Typography>
                    {postCollection.isLoading || postCollection.items.length > 0 ? (
                        <ResizableGrid
                            key={`${postCollection.items[0]?.id}-virtual-grid`}
                            items={postCollection.items}
                            bottomYMargin={BOTTOM_Y_MARGIN}
                            containerRef={modalContentRef}
                            loadMore={postCollection.onLoadNext}
                            getItemHeight={getItemHeight}
                            renderItem={renderItem}
                        />
                    ) : (
                        <SearchNotFound mx="auto" />
                    )}
                </Flex>
            </Flex>
        </Flex>
    );
};
